<template>
  <span ref="chunkRef" class="word-chunk">
    <template v-if="shouldRender">
      <span 
        v-for="(word, localIndex) in chunk" 
        :key="localIndex"
        class="word-item"
        :class="{ 
          'deleted': word.deleted, 
          'selected': isWordSelected(word._originalIndex)
        }"
        :data-word-id="segmentIndex + '-' + word._originalIndex"
        @click.stop="onWordClick($event, word)"
        @mousedown.left="onWordMouseDown($event, word)"
        @mouseover="onWordMouseOver($event, word)"
        :title="formatTime(word.start_time) + ' - ' + formatTime(word.end_time)"
      >
        <template v-if="isWordSelected(word._originalIndex) && selectedWordLocationsLength === 1">
            <div 
              v-if="word._originalIndex > 0" 
              class="split-btn-floating" 
              @click.stop="onSplitSegment(word._originalIndex)"
              title="从此字之前断开为两段 (Enter)"
            >
              <i class="mdi mdi-keyboard-return"></i>
            </div>
            <div 
              v-else-if="word._originalIndex === 0 && segmentIndex > 0 && isMergeable" 
              class="split-btn-floating" 
              @click.stop="onMergeSegment"
              title="与上一段合并 (Backspace)"
            >
              <i class="mdi mdi-backspace"></i>
            </div>
        </template>
        {{ getWordText(word) }}
      </span>
    </template>
    <template v-else>
      <span class="chunk-placeholder" :style="{ height: placeholderHeight + 'px', display: 'inline-block', width: '100%' }">
        <!-- 仅作为占位符，撑开高度，避免滚动条抖动 -->
      </span>
    </template>
  </span>
</template>

<script>
export default {
  name: 'WordChunk',
  props: {
    chunk: {
      type: Array,
      required: true
    },
    segmentIndex: {
      type: Number,
      required: true
    },
    groupIndex: {
      type: Number,
      required: true
    },
    selectedWordLocationsLength: {
      type: Number,
      default: 0
    },
    isMergeable: {
      type: Boolean,
      default: false
    },
    selectedWordSet: {
      type: Object,
      required: true
    },
    playingVideoId: {
      type: [String, Number],
      default: null
    },
    currentTime: {
      type: Number,
      default: 0
    }
  },
  emits: ['word-click', 'word-mousedown', 'word-mouseover', 'split-segment', 'merge-segment'],
  data() {
    return {
      isVisible: true, // 初始默认可见，交由 Observer 判断
      placeholderHeight: 0,
      observer: null
    }
  },
  computed: {
    shouldRender() {
      // 1. 如果在屏幕内，渲染
      if (this.isVisible) return true;
      
      // 2. 如果正在播放该视频，且当前时间距离该 Chunk 很近，强制渲染，保证高亮查找逻辑能够找到 DOM
      if (this.playingVideoId && this.chunk.length > 0) {
          const firstWord = this.chunk[0];
          const lastWord = this.chunk[this.chunk.length - 1];
          const timeMs = this.currentTime * 1000;
          // 提前 3 秒缓冲
          if (timeMs >= firstWord.start_time - 3000 && timeMs <= lastWord.end_time + 3000) {
              return true;
          }
      }
      return false;
    }
  },
  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        this.isVisible = true;
        this.placeholderHeight = 0;
      } else {
        if (this.isVisible && this.$refs.chunkRef) {
          // 在隐藏前记录当前高度，用于占位
          this.placeholderHeight = this.$refs.chunkRef.offsetHeight;
        }
        this.isVisible = false;
      }
    }, { 
        rootMargin: '600px 0px' // 扩大上下预加载范围，600px 约等于半屏
    });
    
    if (this.$refs.chunkRef) {
      this.observer.observe(this.$refs.chunkRef);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    isWordSelected(wIndex) {
      return this.selectedWordSet.has(`${this.segmentIndex}-${wIndex}`);
    },
    onWordClick(event, word) {
        this.$emit('word-click', { event, groupIndex: this.groupIndex, segmentIndex: this.segmentIndex, wordIndex: word._originalIndex, word });
    },
    onWordMouseDown(event, word) {
        this.$emit('word-mousedown', { event, groupIndex: this.groupIndex, segmentIndex: this.segmentIndex, wordIndex: word._originalIndex, word });
    },
    onWordMouseOver(event, word) {
        this.$emit('word-mouseover', { event, groupIndex: this.groupIndex, segmentIndex: this.segmentIndex, wordIndex: word._originalIndex, word });
    },
    onSplitSegment(wordIndex) {
        this.$emit('split-segment', { groupIndex: this.groupIndex, segmentIndex: this.segmentIndex, wordIndex });
    },
    onMergeSegment() {
        this.$emit('merge-segment', { groupIndex: this.groupIndex, segmentIndex: this.segmentIndex });
    },
    getWordText(word) {
      if (word.text) return word.text;
      if (word.start_time != null && word.end_time != null) {
          const duration = (Number(word.end_time) - Number(word.start_time)) / 1000;
          if (duration > 0) {
             return `[空${duration.toFixed(1)}秒]`;
          }
      }
      return '[空]';
    },
    formatTime(ms) {
      if (!ms && ms !== 0) return '00:00'
      const s = Math.floor(ms / 1000)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return `${m}:${sec.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.word-chunk {
  display: inline;
}

.word-item {
    font-size: 0.85em;
    padding: 1px 2px;
    margin: 0;
    cursor: text;
    border-radius: 0;
    position: relative;
    display: inline-block;
}

.word-item:hover {
    background-color: #e3f2fd;
}

.word-item.deleted {
    text-decoration: line-through;
    color: #999;
    background-color: #eee;
}

/* JavaScript 会动态添加此 class */
.word-item.playing {
    color: #ff5252;
    font-weight: bold;
    background-color: #fff3e0;
}

.word-item.selected {
    background-color: #2196f3;
    color: white;
}

.word-item.selected.deleted {
    color: rgba(255, 255, 255, 0.6);
}

.split-btn-floating {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #2196f3;
    color: #fff;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    margin-bottom: 2px;
}

.split-btn-floating:hover {
    background: #1976d2;
}

.chunk-placeholder {
  background-color: transparent;
}
</style>
